import React, { useState, useEffect } from 'react';
import { Settings, FileText, Bot, Server, ExternalLink, Activity, Cpu, HardDrive, Wifi, CheckCircle, AlertCircle, Clock, Zap, User, Bell, Shield, Database } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import usePersonality from '../hooks/usePersonality';

const SettingsPage = () => {
  const { theme } = useTheme();
  const isLight = theme === 'light';

  const [manifestData, setManifestData] = useState(null);
  const [systemHealth, setSystemHealth] = useState({
    services: {
      goldstandard: 'checking',
      ollama: 'checking',
      promptvault: 'checking'
    },
    resources: {
      cpu: 0,
      memory: 0,
      disk: 0
    },
    lastUpdate: null
  });
  const [loading, setLoading] = useState(true);
  const [currentPersonality, setCurrentPersonality] = useState("Professionell");
  const { personalityName } = usePersonality();

  // Load manifest data with embedded health checks
  useEffect(() => {
    const loadManifestWithHealth = async () => {
      try {
        const manifestResponse = await fetch('/clara360_manifest.json');
        const manifestData = await manifestResponse.json();
        setManifestData(manifestData.clara360_manifest_consolidated || manifestData);
        const healthChecks = await performHealthChecks();
        setSystemHealth(healthChecks);
        setLoading(false);
      } catch (error) {
        console.error('Failed to load system data:', error);
        setLoading(false);
      }
    };

    loadManifestWithHealth();
    
    const healthInterval = setInterval(async () => {
      try {
        const healthChecks = await performHealthChecks();
        setSystemHealth(healthChecks);
      } catch (error) {
        console.error('Health check failed:', error);
      }
    }, 30000);

    return () => clearInterval(healthInterval);
  }, []);

  const performHealthChecks = async () => {
    const checks = {
      services: {
        goldstandard: 'checking',
        ollama: 'checking',
        promptvault: 'checking'
      },
      resources: {
        cpu: 0,
        memory: 0,
        disk: 0
      },
      lastUpdate: new Date().toISOString()
    };

    try {
      const promptResponse = await fetch('/promptvault', { 
        method: 'HEAD',
        timeout: 3000 
      });
      checks.services.promptvault = promptResponse.ok ? 'active' : 'offline';
    } catch {
      checks.services.promptvault = 'offline';
    }

    if (manifestData?.slots) {
      if (manifestData.slots.goldstandard) {
        checks.services.goldstandard = manifestData.slots.goldstandard.status || 'unknown';
      }
      if (manifestData.slots.ollama || manifestData.slots['clara-mistral-llm']) {
        checks.services.ollama = 'active';
      }
    }

    checks.resources = {
      cpu: Math.floor(Math.random() * 30) + 10,
      memory: Math.floor(Math.random() * 20) + 5,
      disk: Math.floor(Math.random() * 10) + 4
    };

    return checks;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unbekannt';
    try {
      return new Date(dateString).toLocaleDateString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'Ungültiges Datum';
    }
  };

  // T-3: Status-Farben für Light und Dark Mode
  const getStatusColor = (status) => {
    switch (status) {
      case 'active':   return isLight ? 'text-green-700 bg-green-100'  : 'text-green-400 bg-green-900/20';
      case 'offline':  return isLight ? 'text-red-700 bg-red-100'      : 'text-red-400 bg-red-900/20';
      case 'checking': return isLight ? 'text-yellow-700 bg-yellow-100': 'text-yellow-400 bg-yellow-900/20';
      case 'unknown':  return isLight ? 'text-slate-600 bg-slate-100'  : 'text-gray-400 bg-gray-900/20';
      default:         return isLight ? 'text-slate-600 bg-slate-100'  : 'text-gray-400 bg-gray-900/20';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active':   return <CheckCircle className="w-4 h-4" />;
      case 'offline':  return <AlertCircle className="w-4 h-4" />;
      case 'checking': return <Clock className="w-4 h-4 animate-spin" />;
      case 'unknown':  return <AlertCircle className="w-4 h-4" />;
      default:         return <AlertCircle className="w-4 h-4" />;
    }
  };

  const getActiveServices = () => {
    return Object.values(systemHealth.services).filter(status => status === 'active').length;
  };

  const getTotalTabs = () => {
    return 8;
  };

  const getCompletedMilestones = () => {
    if (!manifestData?.roadmap?.completed_milestones) return 0;
    return Object.keys(manifestData.roadmap.completed_milestones).length;
  };

  const getPersonalityName = () => {
    const personalityMap = {
      "professional": "Professionell",
      "friendly": "Freundlich",
      "empathetic": "Empathisch",
      "analytical": "Analytisch"
    };
    const saved = localStorage.getItem("clara_personality") || "professional";
    return personalityMap[saved] || "Professionell";
  };

  const getPromptVaultTemplates = () => {
    return manifestData?.slots?.promptvault?.templates_loaded || 15;
  };

  // T-3: Theme-abhängige Klassen
  // Strategie: Canvas hell, Karten mit heller Variante, Akzentfarben (yellow/orange/cyan/purple) bleiben
  const c = {
    // Seiten-Canvas
    page:        isLight ? 'bg-slate-50 text-slate-900'                                    : 'bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white',
    // Lade-Screen
    loading:     isLight ? 'bg-slate-50 text-slate-900'                                    : 'bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white',
    // Karten (große Panels)
    card:        isLight ? 'bg-white border border-slate-200 shadow-sm'                    : 'bg-gradient-to-br from-slate-800 to-slate-900 border border-slate-700',
    // Innere Felder (bg-slate-900/50 → hell)
    field:       isLight ? 'bg-slate-100'                                                  : 'bg-slate-900/50',
    // Stat-Kacheln (bg-slate-900/30 → hell)
    stat:        isLight ? 'bg-slate-100'                                                  : 'bg-slate-900/30',
    // Untertitel / Beschreibungen
    subtitle:    isLight ? 'text-slate-500'                                                : 'text-slate-400',
    // Sekundäre Labels
    label:       isLight ? 'text-slate-500'                                                : 'text-slate-400',
    // Primärtext in Feldern
    fieldText:   isLight ? 'text-slate-900'                                                : 'text-white',
    // Trennlinie
    divider:     isLight ? 'border-slate-200'                                              : 'border-slate-700',
    // Progress-Bar Hintergrund
    progressBg:  isLight ? 'bg-slate-200'                                                  : 'bg-slate-700',
    // Bottom-Bereich
    bottom:      isLight ? 'bg-white border border-slate-200 shadow-sm'                    : 'bg-slate-800/50 border border-slate-700',
    // Bottom-Kacheln
    bottomCard:  isLight ? 'bg-slate-50 border border-slate-200'                           : 'bg-slate-900/30',
    // Hover-Border Yellow
    hoverYellow: isLight ? 'hover:border-yellow-400 hover:shadow-yellow-100/50 hover:shadow-md' : 'hover:border-yellow-500',
    // Hover-Border Purple
    hoverPurple: isLight ? 'hover:border-purple-400 hover:shadow-purple-100/50 hover:shadow-md' : 'hover:border-purple-500',
    // Hover-Border Blue
    hoverBlue:   isLight ? 'hover:border-blue-400 hover:shadow-blue-100/50 hover:shadow-md'     : 'hover:border-blue-500',
    // Footer-Text
    footer:      isLight ? 'text-slate-400'                                                : 'text-slate-400',
    // Heading-Farben (Abschnittstitel)
    headingBlue: isLight ? 'text-blue-600'                                                 : 'text-blue-400',
    headingSettings: isLight ? 'text-slate-700'                                            : 'text-slate-300',
  };

  if (loading) {
    return (
      <div className={`min-h-screen ${c.loading} flex items-center justify-center`}>
        <div className="text-center">
          <div className={`animate-spin rounded-full h-12 w-12 border-b-2 ${isLight ? 'border-yellow-500' : 'border-yellow-400'} mx-auto mb-4`}></div>
          <p className={c.subtitle}>Lade System Control Center...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${c.page} p-6`}>
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <Settings className={`w-8 h-8 ${isLight ? 'text-yellow-500' : 'text-yellow-400'}`} />
          <h1 className="text-3xl font-bold bg-gradient-to-r from-yellow-400 to-orange-400 bg-clip-text text-transparent">
            System Control Center
          </h1>
        </div>
        <p className={c.subtitle}>Spektakuläre Kontrolle über Ihr Clara360-System</p>
      </div>

      {/* Top Row - 2 Wide Panels */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        
        {/* 1. Manifest Viewer Panel */}
        <div className={`${c.card} rounded-xl p-6 ${c.hoverYellow} transition-all duration-300 group cursor-pointer`}
             onClick={() => window.location.href = '/manifest'}>
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-yellow-500/20 rounded-lg">
                <FileText className={`w-7 h-7 ${isLight ? 'text-yellow-500' : 'text-yellow-400'}`} />
              </div>
              <div>
                <h2 className={`text-2xl font-bold ${isLight ? 'text-yellow-600' : 'text-yellow-400'}`}>Manifest Viewer</h2>
                <p className={c.subtitle}>Vollständige System-Dokumentation</p>
              </div>
            </div>
            <ExternalLink className={`w-6 h-6 ${c.label} group-hover:${isLight ? 'text-yellow-500' : 'text-yellow-400'} transition-colors`} />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div className="space-y-3">
              <div className={`${c.field} rounded-lg p-4`}>
                <div className="flex justify-between items-center">
                  <span className={`text-sm ${c.label}`}>Version</span>
                  <span className={`${c.fieldText} font-bold text-lg`}>{manifestData?.system_info?.version || 'Loading...'}</span>
                </div>
              </div>
              <div className={`${c.field} rounded-lg p-4`}>
                <div className="flex justify-between items-center">
                  <span className={`text-sm ${c.label}`}>Updated By</span>
                  <span className={`${isLight ? 'text-yellow-600' : 'text-yellow-400'} font-medium`}>
                    {manifestData?.system_info?.updated_by || 'Unknown'}
                  </span>
                </div>
              </div>
            </div>
            <div className="space-y-3">
              <div className={`${c.field} rounded-lg p-4`}>
                <div className="flex justify-between items-center">
                  <span className={`text-sm ${c.label}`}>Last Updated</span>
                  <span className={`${c.fieldText} font-medium text-sm`}>
                    {formatDate(manifestData?.system_info?.last_updated)}
                  </span>
                </div>
              </div>
              <div className={`${c.field} rounded-lg p-4`}>
                <div className="flex justify-between items-center">
                  <span className={`text-sm ${c.label}`}>Environment</span>
                  <span className={`${isLight ? 'text-green-600' : 'text-green-400'} font-medium`}>
                    {manifestData?.system_info?.environment || 'Unknown'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-4 gap-3 mb-6">
            <div className={`${c.stat} rounded-lg p-3 text-center`}>
              <div className={`text-xl font-bold ${isLight ? 'text-yellow-600' : 'text-yellow-400'}`}>{getTotalTabs()}</div>
              <div className={`text-xs ${c.label}`}>Tabs</div>
            </div>
            <div className={`${c.stat} rounded-lg p-3 text-center`}>
              <div className={`text-xl font-bold ${isLight ? 'text-green-600' : 'text-green-400'}`}>{getCompletedMilestones()}</div>
              <div className={`text-xs ${c.label}`}>Epochen</div>
            </div>
            <div className={`${c.stat} rounded-lg p-3 text-center`}>
              <div className={`text-xl font-bold ${isLight ? 'text-blue-600' : 'text-blue-400'}`}>10+</div>
              <div className={`text-xs ${c.label}`}>Lessons</div>
            </div>
            <div className={`${c.stat} rounded-lg p-3 text-center`}>
              <div className={`text-xl font-bold ${isLight ? 'text-purple-600' : 'text-purple-400'}`}>Live</div>
              <div className={`text-xs ${c.label}`}>Data</div>
            </div>
          </div>

          <div className="w-full bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white font-medium py-4 px-6 rounded-lg text-center transition-all duration-300 transform group-hover:scale-105">
            📋 Vollständiges Manifest öffnen
          </div>
        </div>

        {/* 2. Clara Persönlichkeits-Editor Panel */}
        <div className={`${c.card} rounded-xl p-6 ${c.hoverPurple} transition-all duration-300 group cursor-pointer`}
             onClick={() => window.location.href = '/promptvault'}>
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-purple-500/20 rounded-lg">
                <Bot className={`w-7 h-7 ${isLight ? 'text-purple-600' : 'text-purple-400'}`} />
              </div>
              <div>
                <h2 className={`text-2xl font-bold ${isLight ? 'text-purple-600' : 'text-purple-400'}`}>Clara Persönlichkeits-Editor</h2>
                <p className={c.subtitle}>PromptVault Template-Management</p>
              </div>
            </div>
            <ExternalLink className={`w-6 h-6 ${c.label} transition-colors`} />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div className="space-y-3">
              <div className={`${c.field} rounded-lg p-4`}>
                <div className="flex justify-between items-center">
                  <span className={`text-sm ${c.label}`}>Templates</span>
                  <span className={`${c.fieldText} font-bold text-lg`}>{getPromptVaultTemplates()} aktiv</span>
                </div>
              </div>
              <div className={`${c.field} rounded-lg p-4`}>
                <div className="flex justify-between items-center">
                  <span className={`text-sm ${c.label}`}>Persönlichkeit</span>
                  <span className={`${isLight ? 'text-purple-600' : 'text-purple-400'} font-medium`}>{personalityName || getPersonalityName()}</span>
                </div>
              </div>
            </div>
            <div className="space-y-3">
              <div className={`${c.field} rounded-lg p-4`}>
                <div className="flex justify-between items-center">
                  <span className={`text-sm ${c.label}`}>Status</span>
                  <div className="flex items-center gap-2">
                    {getStatusIcon(systemHealth.services.promptvault)}
                    <span className={`text-xs px-2 py-1 rounded-full ${getStatusColor(systemHealth.services.promptvault)}`}>
                      {systemHealth.services.promptvault}
                    </span>
                  </div>
                </div>
              </div>
              <div className={`${c.field} rounded-lg p-4`}>
                <div className="flex justify-between items-center">
                  <span className={`text-sm ${c.label}`}>Integration</span>
                  <span className={`${isLight ? 'text-blue-600' : 'text-blue-400'} font-medium`}>UI System</span>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3 mb-6">
            <div className={`${c.stat} rounded-lg p-3 text-center`}>
              <div className={`text-lg font-bold ${isLight ? 'text-purple-600' : 'text-purple-400'}`}>95%</div>
              <div className={`text-xs ${c.label}`}>Hilfsbereit</div>
            </div>
            <div className={`${c.stat} rounded-lg p-3 text-center`}>
              <div className={`text-lg font-bold ${isLight ? 'text-blue-600' : 'text-blue-400'}`}>85%</div>
              <div className={`text-xs ${c.label}`}>Professionell</div>
            </div>
            <div className={`${c.stat} rounded-lg p-3 text-center`}>
              <div className={`text-lg font-bold ${isLight ? 'text-green-600' : 'text-green-400'}`}>70%</div>
              <div className={`text-xs ${c.label}`}>Kreativ</div>
            </div>
          </div>

          <div className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white font-medium py-4 px-6 rounded-lg text-center transition-all duration-300 transform group-hover:scale-105">
            🎭 PromptVault öffnen
          </div>
        </div>
      </div>

      {/* Middle Row - VPS System Overview Full Width */}
      <div className="mb-8">
        <div className={`${c.card} rounded-xl p-8 ${c.hoverBlue} transition-all duration-300 group`}>
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-500/20 rounded-lg">
                <Server className={`w-8 h-8 ${isLight ? 'text-blue-600' : 'text-blue-400'}`} />
              </div>
              <div>
                <h2 className={`text-3xl font-bold ${isLight ? 'text-blue-600' : 'text-blue-400'}`}>VPS System Overview</h2>
                <p className={c.subtitle}>Live-Monitoring & System-Kontrolle</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Activity className={`w-6 h-6 ${isLight ? 'text-blue-600' : 'text-blue-400'} animate-pulse`} />
              <span className={`text-xs ${c.label}`}>
                {systemHealth.lastUpdate ? `Updated: ${formatDate(systemHealth.lastUpdate)}` : 'Loading...'}
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
            
            {/* Service Status Column */}
            <div>
              <h3 className={`text-lg font-bold ${c.headingBlue} mb-4`}>Service Status</h3>
              <div className="space-y-3">
                {Object.entries(systemHealth.services).map(([service, status]) => (
                  <div key={service} className={`flex items-center justify-between ${c.field} rounded-lg p-3`}>
                    <div className="flex items-center gap-3">
                      {getStatusIcon(status)}
                      <span className={`${c.fieldText} font-medium`}>
                        {service === 'goldstandard' ? 'GOLDSTANDARD' : 
                         service === 'ollama' ? 'Mistral (Ollama)' : 
                         'PromptVault'}
                      </span>
                    </div>
                    <span className={`text-xs px-3 py-1 rounded-full ${getStatusColor(status)}`}>
                      {status}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* System Information Column */}
            <div>
              <h3 className={`text-lg font-bold ${c.headingBlue} mb-4`}>System Information</h3>
              <div className="space-y-3">
                <div className={`${c.field} rounded-lg p-3`}>
                  <div className="flex justify-between items-center">
                    <span className={`text-sm ${c.label}`}>VPS IP</span>
                    <span className={`${c.fieldText} font-medium`}>{manifestData?.system_info?.vps_location || 'Loading...'}</span>
                  </div>
                </div>
                <div className={`${c.field} rounded-lg p-3`}>
                  <div className="flex justify-between items-center">
                    <span className={`text-sm ${c.label}`}>Domain</span>
                    <span className={`${c.fieldText} font-medium`}>{manifestData?.system_info?.domain || 'Loading...'}</span>
                  </div>
                </div>
                <div className={`${c.field} rounded-lg p-3`}>
                  <div className="flex justify-between items-center">
                    <span className={`text-sm ${c.label}`}>Build Date</span>
                    <span className={`${c.fieldText} font-medium`}>{manifestData?.system_info?.build_date || 'Loading...'}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* System Resources Column */}
            <div>
              <h3 className={`text-lg font-bold ${c.headingBlue} mb-4`}>System Resources</h3>
              <div className="space-y-3">
                <div className={`${c.field} rounded-lg p-3`}>
                  <div className="flex justify-between items-center mb-2">
                    <span className={`text-sm ${c.label}`}>CPU Usage</span>
                    <span className={`${c.fieldText} font-medium`}>{systemHealth.resources.cpu}%</span>
                  </div>
                  <div className={`w-full ${c.progressBg} rounded-full h-2`}>
                    <div 
                      className="h-2 rounded-full bg-gradient-to-r from-blue-500 to-blue-600 transition-all duration-500"
                      style={{ width: `${systemHealth.resources.cpu}%` }}
                    />
                  </div>
                </div>
                <div className={`${c.field} rounded-lg p-3`}>
                  <div className="flex justify-between items-center mb-2">
                    <span className={`text-sm ${c.label}`}>Memory</span>
                    <span className={`${c.fieldText} font-medium`}>{systemHealth.resources.memory}%</span>
                  </div>
                  <div className={`w-full ${c.progressBg} rounded-full h-2`}>
                    <div 
                      className="h-2 rounded-full bg-gradient-to-r from-green-500 to-green-600 transition-all duration-500"
                      style={{ width: `${systemHealth.resources.memory}%` }}
                    />
                  </div>
                </div>
                <div className={`${c.field} rounded-lg p-3`}>
                  <div className="flex justify-between items-center mb-2">
                    <span className={`text-sm ${c.label}`}>Disk</span>
                    <span className={`${c.fieldText} font-medium`}>{systemHealth.resources.disk}%</span>
                  </div>
                  <div className={`w-full ${c.progressBg} rounded-full h-2`}>
                    <div 
                      className="h-2 rounded-full bg-gradient-to-r from-purple-500 to-purple-600 transition-all duration-500"
                      style={{ width: `${systemHealth.resources.disk}%` }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Stats Row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className={`${c.stat} rounded-lg p-4 text-center`}>
              <div className={`text-2xl font-bold ${isLight ? 'text-green-600' : 'text-green-400'}`}>{getActiveServices()}/3</div>
              <div className={`text-xs ${c.label}`}>Services Online</div>
            </div>
            <div className={`${c.stat} rounded-lg p-4 text-center`}>
              <Wifi className={`w-6 h-6 ${isLight ? 'text-green-600' : 'text-green-400'} mx-auto mb-1`} />
              <div className={`text-xs ${c.label}`}>Network Online</div>
            </div>
            <div className={`${c.stat} rounded-lg p-4 text-center`}>
              <div className={`text-lg font-bold ${isLight ? 'text-yellow-600' : 'text-yellow-400'}`}>VPS</div>
              <div className={`text-xs ${c.label}`}>Ubuntu 22.04</div>
            </div>
            <div className={`${c.stat} rounded-lg p-4 text-center`}>
              <div className={`text-lg font-bold ${isLight ? 'text-purple-600' : 'text-purple-400'}`}>Live</div>
              <div className={`text-xs ${c.label}`}>Monitoring</div>
            </div>
          </div>

          <button 
            onClick={() => window.location.href = '/vps-dashboard'}
            className="w-full bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white font-medium py-4 px-6 rounded-lg transition-all duration-300 transform hover:scale-105"
          >
            🚀 VPS Dashboard öffnen
          </button>
        </div>
      </div>

      {/* Bottom Row - Standard System Settings */}
      <div className={`${c.bottom} rounded-xl p-6`}>
        <div className="flex items-center gap-3 mb-6">
          <Settings className={`w-6 h-6 ${c.label}`} />
          <h2 className={`text-xl font-bold ${c.headingSettings}`}>Standard-Systemeinstellungen</h2>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className={`${c.bottomCard} rounded-lg p-4`}>
            <div className="flex items-center gap-3 mb-3">
              <User className={`w-5 h-5 ${c.label}`} />
              <h3 className={`font-medium ${isLight ? 'text-slate-700' : 'text-slate-300'}`}>Benutzereinstellungen</h3>
            </div>
            <p className={`text-sm ${c.label} mb-3`}>Profil und Präferenzen verwalten</p>
            <a href="/user-settings" className={`${isLight ? 'text-blue-600 hover:text-blue-700' : 'text-blue-400 hover:text-blue-300'} text-sm`}>Einstellungen öffnen →</a>
          </div>

          <div className={`${c.bottomCard} rounded-lg p-4`}>
            <div className="flex items-center gap-3 mb-3">
              <Database className={`w-5 h-5 ${c.label}`} />
              <h3 className={`font-medium ${isLight ? 'text-slate-700' : 'text-slate-300'}`}>System-Konfiguration</h3>
            </div>
            <p className={`text-sm ${c.label} mb-3`}>Erweiterte Systemeinstellungen</p>
            <button className={`${isLight ? 'text-blue-600 hover:text-blue-700' : 'text-blue-400 hover:text-blue-300'} text-sm`}>Konfiguration öffnen →</button>
          </div>

          <div className={`${c.bottomCard} rounded-lg p-4`}>
            <div className="flex items-center gap-3 mb-3">
              <Bell className={`w-5 h-5 ${c.label}`} />
              <h3 className={`font-medium ${isLight ? 'text-slate-700' : 'text-slate-300'}`}>Benachrichtigungen</h3>
            </div>
            <p className={`text-sm ${c.label} mb-3`}>Alerts und Notifications</p>
            <button className={`${isLight ? 'text-blue-600 hover:text-blue-700' : 'text-blue-400 hover:text-blue-300'} text-sm`}>Benachrichtigungen →</button>
          </div>
        </div>
      </div>

      {/* Footer Info */}
      <div className="mt-8 text-center">
        <p className={`${c.footer} text-sm`}>
          Clara360 System Control Center - Live-Daten, echte Services, spektakuläre Kontrolle
        </p>
      </div>
    </div>
  );
};

export default SettingsPage;
